import { ApiService } from '@services/api.service';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { PracticePageResp } from '@/models/practice.model';
import { TablePracticeModel } from '@/models/tables/table-practices.model';
import { catchError, map } from 'rxjs/operators';
import { VwfsFrameModel } from '@/models/imports/vwfs-frame-model';
import { TablePaginationRespModel } from '@/models/table-pagination-resp.model';
import { ImportSummaryModel, ImportTriggerResponse } from '@/models/imports/import-summary.model';

export interface TableDataApiParams {
  sort?: keyof TablePracticeModel | '';
  orderBy?: 'DESC' | 'ASC';
  page?: number;
  size?: number;
}

type TempCustomPracticeResp = Omit<PracticePageResp, 'content'> & {
  content: TablePracticeModel[];
};

@Injectable({
  providedIn: 'root',
})
export class ImportFlowService extends ApiService {
  getCrossF7Data(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/crossF7/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getCrossF7DeletedData(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/crossF7Deleted/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getCrossF8Data(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/crossF8/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getVwfsDashboardData(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/vwfsDashboard/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getVwfsCommissionData(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/vwfsCommission/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getVwbankCommissionData(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/vwbankCommission/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getLoyalty(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/loyalty/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getImportRegistry(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/importRegistry/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getVhmProforma(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/vhmproforma/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getVhmReport(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/vhmreport/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getVwfsFrameData(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TablePaginationRespModel<VwfsFrameModel>> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/vwfsitFrame/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }

  getImportSummary(): Observable<ImportSummaryModel> {
    return this.http.get<ImportSummaryModel>(this.baseUrl + `/importflow/summary`).pipe(
      catchError(() => {
        // Se l'endpoint non è disponibile, restituisci dati mock per sviluppo
        return of({
          totalImports: 0,
          completed: 0,
          inProgress: 0,
          failed: 0,
          pending: 0,
          importsByType: [],
          importsByDate: [],
        });
      }),
    );
  }

  getImportRegistryCount(): Observable<{
    total: number;
    completed: number;
    onWorking: number;
    failed: number;
    waiting: number;
  }> {
    return this.http
      .get<{
        total: number;
        completed: number;
        onWorking: number;
        failed: number;
        waiting: number;
      }>(this.baseUrl + `/importflow/importRegistry/count`)
      .pipe(
        catchError(() => {
          // Se l'endpoint non è disponibile, restituisci dati mock
          return of({
            total: 0,
            completed: 0,
            onWorking: 0,
            failed: 0,
            waiting: 0,
          });
        }),
      );
  }

  triggerManualImport(): Observable<any> {
    return this.http.post(
      this.baseUrl + `/importflow/importRegistry/start`,
      {},
      {
        observe: 'response',
      },
    );
  }

  getOrderSliData(
    params: TableDataApiParams = {
      sort: '',
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<TempCustomPracticeResp> {
    return this.http
      .get<any>(this.baseUrl + `/importflow/orderSli/page`, { params: { ...params } })
      .pipe(
        map(({ content, ...other }) => ({
          ...other,
          content: content,
        })),
        catchError(() => of(null)),
      );
  }
}
